import { z } from "zod/v4";
import {
	absentable,
	audienceInputSchema,
	audienceSchema,
	levelSchema,
	placeInputSchema,
	placeSchema,
	slugSchema,
} from "./catalog";
import { campIdSchema, editionIdSchema, localDateSchema, organizerIdSchema } from "./ids";

export const campStatusSchema = z.enum(["draft", "published", "archived"]);
export type CampStatus = z.infer<typeof campStatusSchema>;

export const campSchema = z.object({
	audience: audienceSchema,
	coverImageUrl: absentable(z.string()),
	description: absentable(z.string()),
	id: campIdSchema,
	level: levelSchema,
	name: z.string(),
	organizerId: organizerIdSchema,
	place: placeSchema,
	slug: z.string(),
	status: campStatusSchema,
});
export type Camp = z.infer<typeof campSchema>;

/** One row of `GET /organizers/{id}/camps`: the camp plus its counts. */
export const campSummarySchema = campSchema.extend({
	counts: z.object({
		editions: z.number().int().nonnegative(),
		nextEdition: z
			.object({
				capacity: z.number().int().positive(),
				id: editionIdSchema,
				startsOn: localDateSchema,
				taken: z.number().int().nonnegative(),
			})
			.nullable(),
		open: z.number().int().nonnegative(),
	}),
});
export type CampSummary = z.infer<typeof campSummarySchema>;
export type NextEdition = NonNullable<CampSummary["counts"]["nextEdition"]>;

export const createCampSchema = z.object({
	audience: audienceInputSchema,
	coverImageUrl: z.url().optional(),
	description: z.string().trim().max(4000).optional(),
	level: levelSchema,
	name: z.string().trim().min(1).max(120),
	place: placeInputSchema,
	slug: slugSchema,
});
export type CreateCamp = z.infer<typeof createCampSchema>;

/** Editing a camp keeps its slug; the body is the create body without it. */
export const updateCampSchema = createCampSchema.omit({ slug: true });
export type UpdateCamp = z.infer<typeof updateCampSchema>;
